import { useState } from 'react'

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react'

import ContentPicture, { type ContentPictureProps } from './ContentPicture'

type Props = ContentPictureProps

export default function ContentImage(props: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const alt = props.alt?.trim() ?? ''

  return (
    <>
      <button
        type='button'
        onClick={() => setIsOpen(true)}
        aria-haspopup='dialog'
        aria-expanded={isOpen}
        aria-label={alt ? `Enlarge image: ${alt}` : 'Enlarge image'}
        className='group relative block w-fit max-w-[min(100%,800px)] cursor-zoom-in focus-visible-ring text-left [&_picture]:contents'
      >
        <ContentPicture {...props} className='w-auto max-w-full' />
        <span
          aria-hidden='true'
          className='pointer-events-none absolute top-1 right-1 z-10 flex h-5 w-5 items-center justify-center border border-white/25 bg-black/70 text-white backdrop-blur-xs transition-colors transition-opacity group-hover:bg-black/90 group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none [@media(hover:hover)_and_(pointer:fine)]:opacity-0'
        >
          <svg
            width='14'
            height='14'
            viewBox='0 0 16 16'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            strokeLinejoin='round'
          >
            <path d='M10 2h4v4M6 14H2v-4M14 2l-4.5 4.5M2 14l4.5-4.5' />
          </svg>
        </span>
      </button>

      <Dialog open={isOpen} onClose={() => setIsOpen(false)} className='relative z-top'>
        <DialogBackdrop
          transition
          className='fixed inset-0 bg-black/85 data-closed:opacity-0 data-enter:duration-150 data-leave:duration-0 motion-reduce:duration-0'
        />

        <div className='fixed inset-0 flex items-center justify-center p-4 sm:p-6'>
          <DialogPanel className='relative flex max-h-full max-w-full items-center justify-center'>
            <DialogTitle className='sr-only'>{alt || 'Enlarged image'}</DialogTitle>
            <ContentPicture
              {...props}
              loading='eager'
              className='mb-0! h-auto! max-h-[calc(100dvh-32px)]! w-auto! max-w-[calc(100vw-32px)]! object-contain! object-center! sm:max-h-[calc(100dvh-48px)]! sm:max-w-[calc(100vw-48px)]!'
            />
          </DialogPanel>
        </div>

        <button
          type='button'
          onClick={() => setIsOpen(false)}
          aria-label='Close'
          className='group fixed top-[calc(env(safe-area-inset-top)+8px)] right-[calc(env(safe-area-inset-right)+8px)] z-10 flex h-8 w-8 items-center justify-center focus-visible-ring text-white'
        >
          <span
            aria-hidden='true'
            className='flex h-6 w-6 items-center justify-center border border-white/25 bg-black/70 backdrop-blur-xs group-hover:bg-black/90'
          >
            <svg
              width='12'
              height='12'
              viewBox='0 0 16 16'
              fill='none'
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <path d='M4 4l8 8M12 4l-8 8' />
            </svg>
          </span>
        </button>
      </Dialog>
    </>
  )
}
